import type { CSSProperties } from 'react';
import { safeAreaMargin, safeAreaPadding } from "@/helpers/safeArea";
import type { Reward } from "@skillscratch/shared-types";
import { FramedGameGrid } from "./FramedGameGrid";
import type { Coordinates, GridColorScheme, SquareInfo } from "./GameGrid/GameGrid";
import { BonusScratchesModal } from "./BonusScratchesModal";
import type { GameThemeId } from "@skillscratch/shared-types";
import { GameTimer } from "./GameTimer";
import { TreasureCount } from "./TreasureCount";
import { RewardsBar } from "./RewardsBar";
import { InfoBar } from "./InfoBar";
import { GameStateProvider } from '@/context/GameStateProvider';
import { useGameState } from '@/hooks/useGameState';
import { BonusBombsModal } from './BonusBombsModal';


export interface ControlColorScheme {
  controlColor: string;
  onControlColor: string;
}

export interface GameColorScheme
  extends GridColorScheme,
    ControlColorScheme {}

export interface GameInfo {
  colorScheme: GameColorScheme;
  scratchLimit: number;
  treasureCount: number;
  /**
   * Time limit in seconds
   */
  timeLimit: number;
  maxRewards: Reward[];
  nameImage: string;
  background: string;
}

export interface GameProps extends GameInfo {
  grid: SquareInfo[][];
  hideGrid?: boolean;
  onSquareReveal: (coordinates: Coordinates) => void;
  onGameEnd: (reason: "no-scratch" | "no-time" | "found-all-treasures") => Promise<void>;
  onGridRectChange?: (rect: DOMRect) => void;
  cardName: GameThemeId;
}

interface GameHeaderProps {
  nameImage: string;
  maxRewards: Reward[];
  colorScheme: GameColorScheme;
}

interface GameBoardProps {
  hideGrid: boolean;
  grid: SquareInfo[][];
  colorScheme: GameColorScheme;
  scratchable: boolean;
  isLoading: boolean;
  scratchesLeft: number;
  onSquareReveal: (coordinates: Coordinates) => void;
  onRectChange?: (rect: DOMRect) => void;
}

interface GameControlsProps {
  timeLimit: number;
  isRunning: boolean;
  timerStartTime: number;
  colorScheme: GameColorScheme;
  treasureFoundCount: number;
  onTimeUp: () => void;
}

interface GameContentProps {
  grid: SquareInfo[][];
  colorScheme: GameColorScheme;
  timeLimit: number;
  maxRewards: Reward[];
  nameImage: string;
  background: string;
  hideGrid?: boolean;
  onGridRectChange?: (rect: DOMRect) => void;
}

const containerStyles: CSSProperties = {
  containerType: 'inline-size',
  position: 'relative',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  paddingTop: safeAreaPadding('top'),
  paddingBottom: safeAreaPadding('bottom'),
} as const;

const headerImageStyles: CSSProperties = {
  width: '50%',
  margin: '0 auto',
  marginBottom: '3cqw',
} as const;

const headerRewardsStyles: CSSProperties = {
  padding: '0 2.5cqw',
  marginBottom: '3cqw',
} as const;

const getGridStyles = (hideGrid: boolean): CSSProperties => ({
  width: '95cqw',
  margin: '4cqh auto',
  transitionProperty: 'opacity, display',
  transitionDuration: '200ms',
  transitionTimingFunction: 'linear',
  transitionBehavior: 'allow-discrete',
  opacity: hideGrid ? 0 : 1,
  display: hideGrid ? 'none' : undefined,
  willChange: 'opacity, display',
});

const controlStyles: CSSProperties = {
  position: 'absolute',
  bottom: '0',
  left: '0',
  right: '0',
  marginBottom: safeAreaMargin('bottom'),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'stretch',
  gap: '2cqw',
} as const;

function GameHeader({ nameImage, maxRewards, colorScheme }: GameHeaderProps) {
  return (
    <div className='mt-6'>
      <img
        src={nameImage}
        style={headerImageStyles}
        alt="name"
      />
      <div style={headerRewardsStyles} className='mt-5'>
        <RewardsBar colorScheme={colorScheme} rewards={maxRewards} />
      </div>
    </div>
  );
}

function GameBoard({
  hideGrid,
  grid,
  colorScheme,
  scratchable,
  isLoading,
  scratchesLeft,
  onSquareReveal,
  onRectChange
}: GameBoardProps) {
  const gridStyles = getGridStyles(hideGrid);

  return (
    <div style={gridStyles}>
      <FramedGameGrid
        scratchable={scratchable}
        grid={grid}
        colorScheme={colorScheme}
        isLoading={isLoading}
        bottomBar={
          <InfoBar
            scratchesLeft={scratchesLeft}
            controlColor={colorScheme.controlColor}
            onControlColor={colorScheme.onControlColor}
          />
        }
        onSquareReveal={onSquareReveal}
        onRectChange={onRectChange}
      />
    </div>
  );
}

function GameControls({
  timeLimit,
  isRunning,
  timerStartTime,
  colorScheme,
  treasureFoundCount,
  onTimeUp
}: GameControlsProps) {
  return (
    <div style={controlStyles}>
      <GameTimer
        timeLimit={timeLimit}
        isRunning={isRunning}
        timerStartTime={timerStartTime}
        controlColor={colorScheme.controlColor}
        onControlColor={colorScheme.onControlColor}
        onTimeUp={onTimeUp}
      />
      <TreasureCount treasureFoundCount={treasureFoundCount} />
    </div>
  );
}

// This component uses the context
function GameContent({
  grid,
  colorScheme,
  timeLimit,
  maxRewards,
  nameImage,
  background,
  hideGrid = false,
  onGridRectChange,
}: GameContentProps) {
  const {
    gameStatus,
    showBoardLoading,
    timerStartTime,
    bonusScratches,
    showBonusScratchesModal,
    scratchesLeft,
    treasureFoundCount,
    handleSquareReveal,
    activateBonusScratches,
    onTimeUp,
    bonusBombs,
    showBonusBombsModal,
    activateBonusBombs,
  } = useGameState();

  return (
    <div style={{ ...containerStyles, background }}>
        <GameHeader 
          nameImage={nameImage} 
          maxRewards={maxRewards} 
          colorScheme={colorScheme} 
        />
        
        <GameBoard
          hideGrid={hideGrid}
          grid={grid}
          colorScheme={colorScheme}
          scratchable={gameStatus !== 'no-scratch' && scratchesLeft > 0}
          isLoading={showBoardLoading}
          scratchesLeft={scratchesLeft}
          onSquareReveal={handleSquareReveal}
          onRectChange={onGridRectChange}
        />

        <GameControls
          timeLimit={timeLimit}
          isRunning={gameStatus === 'playing'}
          timerStartTime={timerStartTime}
          colorScheme={colorScheme}
          treasureFoundCount={treasureFoundCount}
          onTimeUp={onTimeUp}
        />

        <BonusScratchesModal 
          show={showBonusScratchesModal}
          bonusScratches={bonusScratches}
          onClick={activateBonusScratches}
        />

        <BonusBombsModal 
          show={showBonusBombsModal}
          bonusBombs={bonusBombs}
          onClick={() => activateBonusBombs(bonusBombs ?? 0)}
        />
    </div>
  );
}

// Main Game component that sets up the provider
export function Game({
  grid,
  colorScheme,
  scratchLimit,
  treasureCount,
  timeLimit,
  maxRewards,
  nameImage,
  background,
  hideGrid = false,
  onSquareReveal,
  onGameEnd,
  onGridRectChange,
  cardName,
}: GameProps) {
  return (
    <GameStateProvider
      grid={grid}
      scratchLimit={scratchLimit}
      treasureCount={treasureCount}
      onSquareReveal={onSquareReveal}
      onGameEnd={onGameEnd}
      cardName={cardName}
    >
      <GameContent
        grid={grid}
        colorScheme={colorScheme}
        timeLimit={timeLimit}
        maxRewards={maxRewards}
        nameImage={nameImage}
        background={background}
        hideGrid={hideGrid}
        onGridRectChange={onGridRectChange}
      />
    </GameStateProvider>
  );
}